import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import IExternalVideo from '@models/IExternalVideo';
import classNames from 'classnames';
import { Dispatch, SetStateAction, useState } from 'react';
import SwiperType from 'swiper';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface Props {
  readonly playlistId: string;
  readonly setActiveSource?: Dispatch<SetStateAction<string>>;
  readonly setActiveSourceIndex?: Dispatch<SetStateAction<number>>;
  readonly setPlaying?: Dispatch<SetStateAction<boolean>>;
  readonly videoPlaylist: IExternalVideo[];
}

const DesktopVideoList = ({
  videoPlaylist = [],
  setActiveSource,
  setActiveSourceIndex,
  setPlaying,
  playlistId,
}: Props) => {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const playVideo = (videoId: string, index: number) => {
    setActiveSource?.(videoId);
    setActiveSourceIndex?.(index);
    setPlaying?.(true);
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setIsBeginning(swiper?.isBeginning);
    setIsEnd(swiper?.isEnd);
  };

  const swiperId = `video-playlist-${playlistId}`;

  return (
    <div
      className="desktop relative flex w-full max-w-[400px]"
      data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List"
    >
      <div className="relative w-full overflow-hidden">
        <div className="absolute left-0 top-0 flex h-full flex-col">
          <Swiper
            onSlideChange={(swiper) => handleSlideChange(swiper)}
            onToEdge={(swiper) => handleSlideChange(swiper)}
            direction="vertical"
            slidesPerView="auto"
            spaceBetween={20}
            modules={[Navigation, Mousewheel]}
            mousewheel={{
              forceToAxis: true,
            }}
            navigation={{
              prevEl: `#prevBtn-${swiperId}`,
              nextEl: `#nextBtn-${swiperId}`,
            }}
          >
            {videoPlaylist?.map(
              ({ coverImage, videoId, title, subTitle }, index) => (
                <SwiperSlide
                  className="stretch-card"
                  key={`desktop-video-${title}-${subTitle}`}
                  data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-${index}`}
                >
                  <div className="flex flex-row items-center">
                    <button
                      type="button"
                      className="w-1/2"
                      onClick={() => playVideo(`${videoId}`, index)}
                      data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-${index}-Play-Button`}
                    >
                      <span className="relative flex items-center justify-center px-5">
                        <Image image={coverImage} />
                        <Icon
                          name="play_icon_circle"
                          size="27px"
                          className="absolute"
                        />
                      </span>
                    </button>
                    <div className="flex w-1/2 flex-col pr-2">
                      {title && (
                        <p
                          className="body-four line-clamp-3 text-left text-grey-450 dark:text-grey-400"
                          data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-${index}-Subtitle`}
                        >
                          <span
                            className="body-four text-black-100 dark:text-white"
                            data-testid={`CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-${index}-Title`}
                          >
                            {title}{' '}
                          </span>
                          {subTitle}
                        </p>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              )
            )}
          </Swiper>
        </div>
      </div>
      <div
        id={`prevBtn-${swiperId}`}
        className={classNames(
          'absolute left-0 right-0 top-0 z-20 flex h-16 w-full select-none items-center justify-center bg-gradient-to-b from-white opacity-70 hover:cursor-pointer dark:from-black-100 dark:via-black-100',
          {
            invisible: isBeginning,
          }
        )}
        data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-Prev-Button"
      >
        <Icon
          name="caret_down"
          colorClassName="text-yellow"
          className="rotate-180"
        />
      </div>
      <div
        id={`nextBtn-${swiperId}`}
        className={classNames(
          'absolute bottom-0 left-0 right-0 z-20 flex h-16 w-full select-none items-center justify-center bg-gradient-to-t from-white opacity-70 hover:cursor-pointer dark:from-black-100 dark:via-black-100',
          {
            invisible: isEnd,
          }
        )}
        data-testid="CT-10-B-Video-Embed-Playlist-External-Video-Desktop-Video-List-Item-Next-Button"
      >
        <Icon name="caret_down" colorClassName="text-yellow" />
      </div>
    </div>
  );
};

export default DesktopVideoList;
